import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import { ALL_GALLERY_ITEMS, IMAGES } from '../data/siteData'
import { PAGE_META } from '../data/metaData'
import { getOptimizedImageUrl } from '../lib/imageHelper'
import Lightbox from '../components/Lightbox'
import CtaBanner from '../components/CtaBanner'

const CATEGORIES = [
  { label: 'Alle projekter', key: 'all' },
  { label: 'Nedgravning af trampolin', key: 'nedgravning-af-trampolin' },
  { label: 'Belægning', key: 'belaegning' },
  { label: 'Fundament & sokkel', key: 'fundament-sokkel' },
  { label: 'Gravearbejde', key: 'gravearbejde' }
]

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('all')
  const [items, setItems] = useState([])
  const [activeLightboxIndex, setActiveLightboxIndex] = useState(null)

  const metaTitle = PAGE_META.gallery.title
  const metaDescription = PAGE_META.gallery.description

  useEffect(() => {
    let isMounted = true

    async function loadCMSData() {
      try {
        const res = await fetch('/_api/public/v2/content/galleri')
        if (res.ok) {
          const json = await res.json()
          const cmsGalleries = json.items || []
          const flattened = []

          cmsGalleries.forEach((g) => {
            if (Array.isArray(g.images)) {
              g.images.forEach((imgUrl, idx) => {
                flattened.push({
                  id: `${g.gallery_key || 'g'}-${idx}`,
                  title: g.title,
                  image: imgUrl,
                  caption: g.description,
                  category: g.category,
                  galleryKey: g.gallery_key
                })
              })
            }
          })

          if (flattened.length > 0 && isMounted) {
            setItems(flattened)
            return
          }
        }
      } catch (e) {
        // Fallback
      }

      if (isMounted) {
        setItems(ALL_GALLERY_ITEMS)
      }
    }

    loadCMSData()

    return () => {
      isMounted = false
    }
  }, [])

  const filteredItems =
    activeCategory === 'all'
      ? items
      : items.filter((item) => item.galleryKey === activeCategory)

  return (
    <div>
      <SeoHead
        title={metaTitle}
        description={metaDescription}
        canonicalPath="/galleri"
      />

      {/* Header */}
      <section className="relative bg-stone-950 text-white py-16 sm:py-20 overflow-hidden border-b border-stone-800">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(IMAGES.hero, { width: 1800, quality: 80 })}
            alt="JT Knudsen galleri og udførte opgaver inden for grave- og anlægsarbejde"
            className="w-full h-full object-cover opacity-60 brightness-95"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/50 to-stone-950/20"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-wider mb-3">
              <i className="fa-solid fa-camera" aria-hidden="true"></i>
              <span>Billedgalleri</span>
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight drop-shadow-md">
              Galleri & Udførte opgaver
            </h1>
            <p className="mt-4 text-base sm:text-xl text-stone-200 leading-relaxed drop-shadow-sm">
              Her kan du se et udvalg af projekter udført af <Link to="/" className="text-white font-semibold underline decoration-amber-500 decoration-2 underline-offset-2 hover:text-amber-400 transition-colors">JT Knudsen</Link> med nedgravning af trampoliner, flisebelægning, fundamenter og maskingravning i Østjylland og Djursland.
            </p>
          </div>
        </div>
      </section>

      {/* Filter and Grid */}
      <section className="py-12 sm:py-20 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-10 pb-4 border-b border-stone-200">
            {CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.key
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setActiveCategory(cat.key)}
                  className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-[#015205] text-white shadow-sm'
                      : 'bg-white text-stone-700 hover:bg-stone-100 border border-stone-300'
                  }`}
                >
                  {cat.label}
                </button>
              )
            })}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredItems.map((item, idx) => (
              <div
                key={item.id || idx}
                onClick={() => setActiveLightboxIndex(idx)}
                className="group relative overflow-hidden rounded-xl bg-stone-900 border border-stone-200 shadow-sm cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`Åbn ${item.title} i forstørret visning`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setActiveLightboxIndex(idx)
                  }
                }}
              >
                <div className="aspect-4/3 w-full overflow-hidden bg-stone-800">
                  <img
                    src={getOptimizedImageUrl(item.image, { width: 800, height: 600 })}
                    alt={item.title ? `${item.title} - JT Knudsen` : 'Udført anlægsarbejde af JT Knudsen'}
                    onError={(e) => {
                      if (e.currentTarget.src !== item.image) {
                        e.currentTarget.src = item.image
                      }
                    }}
                    className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                </div>

                <div className="p-4 bg-white border-t border-stone-100">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600 block mb-1">
                    {item.category}
                  </span>
                  <h3 className="font-bold text-base text-stone-900 leading-snug">
                    {item.title}
                  </h3>
                  {item.caption && (
                    <p className="mt-1 text-xs text-stone-600 line-clamp-2">
                      {item.caption}
                    </p>
                  )}
                </div>

                {/* Hover overlay hint */}
                <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-stone-950/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <i className="fa-solid fa-expand text-xs" aria-hidden="true"></i>
                </div>
              </div>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12 text-stone-500">
              <p>Ingen billeder fundet i denne kategori i øjeblikket.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Component */}
      {activeLightboxIndex !== null && (
        <Lightbox
          images={filteredItems}
          activeIndex={activeLightboxIndex}
          onClose={() => setActiveLightboxIndex(null)}
          onSelectIndex={(newIdx) => setActiveLightboxIndex(newIdx)}
        />
      )}

      {/* CTA Banner */}
      <CtaBanner
        title="Kunne du tænke dig et lignende resultat?"
        description="Kontakt Jonas Knudsen og hør nærmere om pris og tidshorisont for din have eller byggeopgave."
        primaryBtnText="Indhent uforpligtende tilbud"
        primaryBtnLink="/kontakt"
      />
    </div>
  )
}
